import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from '@angular/core';
import { forkJoin as observableForkJoin } from 'rxjs';
import { BusinessTransactionType, IBusinessTransactionType } from '../../../foundation/models/enums/business-transaction-type.enum';
import { IJurisdiction, Jurisdiction } from '../../../foundation/models/enums/jurisdiction.enum';
import { BaseComponent } from '../../../shared/base-components/base.component';
import { OneTimeMailingModel } from '../../../titleregistration/components/shared/one-time-mailing/one-time-mailing.model';
import { CustomerPlateOTMAddressModel } from '../../../titleregistration/models/customer-plate-with-otm.model';
import { OwnershipRoleType } from '../../../titleregistration/models/enums/ownership-role-type.enum';
import { IRegistrationStatusType, RegistrationStatusType } from '../../../titleregistration/models/enums/registration-status-type.enum';
import { IRegistrationType, RegistrationType } from '../../../titleregistration/models/enums/registration-type.enum';
import { IRegistrationUseType, RegistrationUseType } from '../../../titleregistration/models/enums/registration-use-type.enum';
import { OwnershipCustomerRelationshipModel } from '../../../titleregistration/models/ownership-customer-relationship.model';
import { RegistrationModel } from '../../../titleregistration/models/registration.model';
import { PlateService } from '../../../titleregistration/services/plate.service';
import { TitleRegistrationService } from '../../../titleregistration/services/titleregistration.service';
import { IVehiclePermitType, VehiclePermitType } from '../../../vehicle/models/enums/vehicle-permit-type.enum';
import { VehiclePermitModel } from '../../../vehicle/models/vehicle-permit.model';
import { VehiclePermitService } from '../../../vehicle/services/vehicle-permit.service';

@Component({
    selector: 'tnr-transaction-type',
    templateUrl: './tnr-transaction-type.html',
})
export class TnRTransactionTypeComponent extends BaseComponent {
    @Input() businessTransactionTypeId: number;
    @Input() customer: any;
    @Input() readOnly: boolean;
    @Output() onBusinessTransactionParamRemoved = new EventEmitter<any>();
    @Output() onBusinessTransactionParamSelected = new EventEmitter<any>();

    public dataLoaded: boolean;
    public isPermitSearchApplicable: boolean;
    public isPlacardSearchApplicable: boolean;
    public isRegistrationSearchApplicable: boolean;
    public isResultsLoaded: boolean;
    public jurisdictions: IJurisdiction[];
    public permitNumber: string;
    public permitTypes: IVehiclePermitType[];
    public placard: CustomerPlateOTMAddressModel;
    public placards: CustomerPlateOTMAddressModel[];
    public registration: RegistrationModel;
    public registrations: RegistrationModel[];
    public registrationStatusTypes: IRegistrationStatusType[];
    public registrationTypes: IRegistrationType[];
    public registrationUseTypes: IRegistrationUseType[];
    public vehiclePermit: VehiclePermitModel;
    public vehiclePermits: VehiclePermitModel[];
    private businessTransactionType: BusinessTransactionType = new BusinessTransactionType();
    private ownershipRoleType: OwnershipRoleType = new OwnershipRoleType();
    private selectedTransactionType: IBusinessTransactionType;
    private selectedTransactionTypeId: number;
    private transactionTypes: IBusinessTransactionType[];

    constructor(
        private titleRegistrationService: TitleRegistrationService,
        private vehiclePermitService: VehiclePermitService,
        private plateService: PlateService,
    ) {
        super();
    }

    ngOnInit() {
        this.registrationStatusTypes = new RegistrationStatusType().GetAll();
        this.registrationUseTypes = new RegistrationUseType().GetAll();
        this.registrationTypes = new RegistrationType().GetAll();
        this.permitTypes = new VehiclePermitType().GetAll();
        this.jurisdictions = new Jurisdiction().GetAll();
        this.transactionTypes = new BusinessTransactionType().GetAll();

        this.resetAll();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['businessTransactionTypeId']) {
            this.resetAll(); // for update
        }
    }

    public resetAll(): void {
        this.dataLoaded = false;
        this.resetTransaction();
        this.onTxnTypeSelected(this.businessTransactionTypeId);
        this.getData();
        this.dataLoaded = true;
    }

    /**
     * keeps track of selected registration and select customer if its not already.
    * @param registration
     */
    public onRegistrationSelected = (registration: RegistrationModel): void => {
        this.registration = registration;
        const businessTransactionObject = { businessTransactionParamId: this.registration.id, businessTransactionItemKeyTypeId: 5 };
        this.onBusinessTransactionParamSelected.emit(businessTransactionObject);
    };

    /**
     * Removes the selected registration from the UI
     */
    public onRemoveRegistration() {
        this.registration = null;
        this.onBusinessTransactionParamRemoved.emit();
    }

    /**
     * Removes the selected permit from the UI
     */
    public onRemovePermit() {
        this.vehiclePermit = null;
    }

    /**
     * keeps track of selected permit.
    * @param permit
     */
    public onPermitSelected(permit: VehiclePermitModel): void {
        this.vehiclePermit = permit;
        const businessTransactionObject = { businessTransactionParamId: this.vehiclePermit.id, businessTransactionItemKeyTypeId: 6 };
        this.onBusinessTransactionParamSelected.emit(businessTransactionObject);
    }

    /**
     * Removes the selected placard from the UI
     */
    public onRemovePlacard() {
        this.placard = null;
    }

    /**
     * keeps track of selected placard.
    * @param placard
     */
    public onPlacardSelected(placard: CustomerPlateOTMAddressModel): void {
        this.placard = placard;
        const businessTransactionObject = { businessTransactionParamId: this.placard.id, businessTransactionItemKeyTypeId: 8 }; // customer plate
        this.onBusinessTransactionParamSelected.emit(businessTransactionObject);
    }

    private onTxnTypeSelected(selectedValueId: number) {
        this.selectedTransactionTypeId = +selectedValueId;
        this.selectedTransactionType = this.transactionTypes.find(s => s.id === this.selectedTransactionTypeId);

        if (this.selectedTransactionType) {
            switch (this.selectedTransactionType.id) {
                case this.businessTransactionType.CancelPermanentPlacard.id:
                case this.businessTransactionType.CancelTemporaryPlacard.id:
                case this.businessTransactionType.RenewPermanentPlacard.id:
                case this.businessTransactionType.ReplacePermanentPlacard.id:
                case this.businessTransactionType.ReplaceTemporaryPlacard.id:
                    this.isPlacardSearchApplicable = true;
                    break;
                case this.businessTransactionType.CancelPermit.id:
                case this.businessTransactionType.ReprintPermit.id:
                case this.businessTransactionType.VoidPermit.id:
                case this.businessTransactionType.CancelTRP.id:
                    this.isPermitSearchApplicable = true;
                    break;
                case this.businessTransactionType.SuspendRegistration.id:
                case this.businessTransactionType.CancelRegistration.id:
                case this.businessTransactionType.DuplicateRegistration.id:
                case this.businessTransactionType.TabReplacement.id:
                case this.businessTransactionType.RenewOHVDecal.id:
                case this.businessTransactionType.DuplicatePlate.id:
                case this.businessTransactionType.ReinstateRegistration.id:
                case this.businessTransactionType.VoidRegistration.id:
                case this.businessTransactionType.RevokeRegistration.id:
                case this.businessTransactionType.RequestRegistrationCredit.id:
                    this.isRegistrationSearchApplicable = true;
                    break;
                default:
                    break;
            }
        }
    }

    /**
     * pulls the data for registrations, placards or permits depending upon the transaction type selected
     *
     */
    private getData(): void {
        if (this.isRegistrationSearchApplicable) {
            return this.getRegistrationSearchByType(+ this.selectedTransactionType.id);
        } else if (this.isPermitSearchApplicable) {
            return this.getPermitData();
        } else if (this.isPlacardSearchApplicable) {
            return this.getPlacardData();
        }
    }

    /**
     * registration type id as integer (passed in using +)
     * @param registrationType
     */
    private getRegistrationSearchByType(registrationTypeid): void {
        switch (registrationTypeid) {
            case this.businessTransactionType.RequestRegistrationCredit.id:
                return this.getCreditRegistrationData();
            case this.businessTransactionType.TabReplacement.id:
                return this.getTabRegistrationData();
            case this.businessTransactionType.DuplicatePlate.id:
                return this.getDuplicatePlateRegistrationData();
            default:
                return this.getRegistrationData();
        }
    }

    /**
     * Get all the placards by customer and filters it depending upon the transaction type selected
     */
    private getPlacardData(): void {
        const self = this;
        if (self.customer) {
            self.plateService.getPlacardsByCustomerNumber(self.customer.customerNumber).subscribe(
                r => {
                    self.placards = Array<CustomerPlateOTMAddressModel>();
                    if (r) {
                        self.placards = r as CustomerPlateOTMAddressModel[];
                        switch (this.selectedTransactionType.id) {

                            case self.businessTransactionType.CancelTemporaryPlacard.id:
                            case self.businessTransactionType.ReplaceTemporaryPlacard.id:
                                self.placards = self.placards.filter(s => +s.plate.plateConfigurationId === 91);
                                break;

                            case self.businessTransactionType.CancelPermanentPlacard.id:
                            case self.businessTransactionType.RenewPermanentPlacard.id:
                            case self.businessTransactionType.ReplacePermanentPlacard.id:

                                self.placards = self.placards.filter(s => +s.plate.plateConfigurationId === 90);
                                break;
                        }
                        self.getPlacardOTMAddress(self.placards);
                    }
                    self.isResultsLoaded = true;
                },
                e => {
                    console.log(e);
                }
            );
        }
    }

    /**
     * Populates the Vehicle permit result set
     */
    private getPermitData(): void {
        const self = this;

        if (self.customer) {
            self.vehiclePermitService.getPermitsByCustomerNumber(self.customer.customerNumber)
                .subscribe(r => {
                    self.vehiclePermits = new Array<VehiclePermitModel>();
                    if (r) {
                        if (Array.isArray(r)) {
                            self.vehiclePermits = <VehiclePermitModel[]> r;
                            self.vehiclePermits = self.vehiclePermits.filter(s => s.vehiclePermitStatusTypeId == 1); // filter the active permits
                        }
                    }
                    self.isResultsLoaded = true;
                },
                err => console.log(err));
        }
    }

    /**
     * Populates the registration result set
    */
    private getRegistrationData(): void {
        const self = this;
        if (self.customer) {
            self.titleRegistrationService.getRegistrationsByCustomer(self.customer.customerNumber).subscribe(
                r => {
                    self.registrations = r;
                    self.setCustomerNames();
                    self.isResultsLoaded = true;
                },
                e => {
                    console.log(e);
                }
            );
        }
    }

    private getTabRegistrationData(): void {
        let self = this;
        if (self.customer) {
            self.titleRegistrationService.getValidRegistrationsByCustomer(self.customer.customerNumber).subscribe(
                r => {
                    self.registrations = r;
                    self.setCustomerNames();
                    self.isResultsLoaded = true;
                },
                e => {
                    console.log(e);
                }
            );
        }
    }

    private getDuplicatePlateRegistrationData(): void {
        let self = this;
        if (self.customer) {
            self.titleRegistrationService.getValidSpecialtyPlateRegistrationsByCustomer(self.customer.customerNumber).subscribe(
                r => {
                    self.registrations = r;
                    self.setCustomerNames();
                    self.isResultsLoaded = true;
                },
                e => {
                    console.log(e);
                }
            );
        }
    }

    private getCreditRegistrationData(): void {
        const self = this;
        if (self.customer) {
            self.titleRegistrationService.getCreditRegistrationsByCustomer(self.customer.customerNumber).subscribe(
                r => {
                    self.registrations = r;
                    self.setCustomerNames();
                    self.isResultsLoaded = true;
                },
                e => {
                    console.log(e);
                }
            );
        }
    }

    private setCustomerNames(): void {
        for (let i = 0; i < this.registrations.length; i++) {
            const owners: OwnershipCustomerRelationshipModel[] = this.registrations[i].vehicleOwnershipPlate && this.registrations[i].vehicleOwnershipPlate.vehicleOwnership.ownershipCustomerRelationships.filter(c => c.ownershipRoleTypeID === this.ownershipRoleType.Owner.id);
            if (owners && owners.length > 0) {
                if (owners.length > 1) {
                    owners.sort((n1, n2) => n1.ownershipRoleSequence - n2.ownershipRoleSequence);
                }

                this.pushOwnerName(this.registrations[i], owners[0].customer.customerName, owners[0].customer.customerNumberID);
            } else {
                const lessors: OwnershipCustomerRelationshipModel[] = this.registrations[i].vehicleOwnershipPlate && this.registrations[i].vehicleOwnershipPlate.vehicleOwnership.ownershipCustomerRelationships.filter(c => c.ownershipRoleTypeID === this.ownershipRoleType.Lessor.id);
                if (lessors && lessors.length > 0) {
                    if (lessors.length > 1) {
                        lessors.sort((n1, n2) => n1.ownershipRoleSequence - n2.ownershipRoleSequence);
                    }

                    this.pushOwnerName(this.registrations[i], lessors[0].customer.customerName, lessors[0].customer.customerNumberID);
                }
            }
        }
    }

    /**
     * Dynamically add the customer name to the registration object. This is done without any TypeScript types to
     * allow dynamic adding without transpilation errors since customerId property does not exist in the model.
     * @param registration: Registration object.
     * @param customerName: Customer name.
     * @param customerId: Customer Id.
     */
    private pushOwnerName(registration, customerName, customerId): void {
        registration.customerName = customerName;
        registration.customerNumberID = customerId;
    }

    /**
    * Resets all the objects and flags
    */
    private resetTransaction(): void {

        this.isPermitSearchApplicable = false;
        this.isPlacardSearchApplicable = false;
        this.isRegistrationSearchApplicable = false;
        this.isResultsLoaded = false;
        this.registrations = null;
        this.registration = null;
        this.placard = null;
        this.placards = null;
        this.vehiclePermits = null;
        this.vehiclePermit = null;
    }

    /**
     * Gets the OTM for placards in search result
     * @param placards
     */
    private getPlacardOTMAddress(placards: CustomerPlateOTMAddressModel[]) {
        // use forkjoin here
        const observableBatch = [];
        placards.forEach(f => {
            observableBatch.push(this.titleRegistrationService.getOneTimeMailingBy(f.id, 9)); // TODO : Change it to get ISSUE_PERMANENT_PLACARD_TRANSACTION_TYPE
        });
        observableForkJoin(observableBatch).subscribe(
            result => {
                if (result) {
                    const array = result as OneTimeMailingModel[][];
                    for (let i = 0; i <= placards.length; i++) {
                        if (array[i]) {
                            placards[i].onetimeMailingAddress = array[i][0];
                        }
                    }
                }
            },
            err => {
                this.handleValidationResult(err);
            }
        );
    }

    /**
     * Gets the string representation of the placard type.
     * @param plateConfigurationId: Plate type for the placard.
     * @returns: string representation of the placard type.
     */
    getPlacardType(plateConfigurationId: number): string {
        if (plateConfigurationId === 90) { // refer 45075
            return 'Permanent'; // DisabilityPlacardPermanentLG
        } else {
            return 'Temporary'; // DisabilityPlacardTemporaryLG
        }
    }
}
